import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { CheckCircle, PenLine, Type, Calendar, CheckSquare, X, RotateCcw, ArrowDown, Mail, ShieldCheck, FileCheck } from 'lucide-react';
import * as pdfjsLib from 'pdfjs-dist';
import { toast } from 'sonner';
import AnnotationLayer from '../components/AnnotationLayer';

pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://unpkg.com/pdfjs-dist@3.11.174/legacy/build/pdf.worker.min.js';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const FIELD_STYLES = {
  signature: { border: '#1d4ed8', bg: 'rgba(29,78,216,0.08)', text: '#1d4ed8' },
  initials:  { border: '#0891b2', bg: 'rgba(8,145,178,0.08)',  text: '#0891b2' },
  text:      { border: '#7c3aed', bg: 'rgba(124,58,237,0.08)', text: '#7c3aed' },
  date:      { border: '#059669', bg: 'rgba(5,150,105,0.08)',  text: '#059669' },
  name:      { border: '#0f766e', bg: 'rgba(15,118,110,0.08)', text: '#0f766e' },
  checkbox:  { border: '#d97706', bg: 'rgba(217,119,6,0.08)',  text: '#d97706' },
};

// ─── Signature Modal ──────────────────────────────────────────────────────────
function SignatureModal({ field, onAccept, onClose }) {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [typedSig, setTypedSig] = useState('');
  const [mode, setMode] = useState('draw');

  const getPos = (e, canvas) => {
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return { x: (clientX - rect.left) * (canvas.width / rect.width), y: (clientY - rect.top) * (canvas.height / rect.height) };
  };

  const startDraw = (e) => { e.preventDefault(); setIsDrawing(true); const c = canvasRef.current; const ctx = c.getContext('2d'); const p = getPos(e, c); ctx.beginPath(); ctx.moveTo(p.x, p.y); };
  const draw = (e) => { e.preventDefault(); if (!isDrawing) return; const c = canvasRef.current; const ctx = c.getContext('2d'); ctx.lineWidth = 2.5; ctx.lineCap = 'round'; ctx.strokeStyle = '#0f172a'; const p = getPos(e, c); ctx.lineTo(p.x, p.y); ctx.stroke(); };
  const clear = () => { const c = canvasRef.current; c.getContext('2d').clearRect(0, 0, c.width, c.height); };

  const accept = () => {
    if (mode === 'type') {
      if (!typedSig.trim()) { toast.error('Please type your signature'); return; }
      const c = document.createElement('canvas'); c.width = 400; c.height = 120;
      const ctx = c.getContext('2d'); ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, 400, 120);
      ctx.font = 'italic 44px Georgia, serif'; ctx.fillStyle = '#0f172a'; ctx.textAlign = 'center'; ctx.fillText(typedSig, 200, 75);
      onAccept(field.id, c.toDataURL('image/png'));
    } else {
      const c = canvasRef.current; const data = c.getContext('2d').getImageData(0, 0, c.width, c.height).data;
      if (!data.some((v, i) => i % 4 === 3 && v > 0)) { toast.error('Please draw your signature'); return; }
      onAccept(field.id, c.toDataURL('image/png'));
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 flex items-center justify-center p-4" data-testid="signature-modal">
      <div className="bg-white rounded-sm shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
          <h3 className="font-semibold text-slate-900" style={{ fontFamily: 'Outfit' }}>Add {field.type === 'initials' ? 'Initials' : 'Signature'}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
        </div>
        <div className="p-5">
          <div className="flex gap-2 mb-4">
            {['draw', 'type'].map(m => (
              <button key={m} onClick={() => setMode(m)} className={`px-4 py-1.5 rounded-sm text-sm font-medium transition-colors capitalize ${mode === m ? 'bg-blue-700 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`} data-testid={`sig-mode-${m}`}>{m}</button>
            ))}
          </div>
          {mode === 'draw' ? (
            <div>
              <canvas ref={canvasRef} width={400} height={160} className="signature-canvas w-full border border-slate-300 rounded-sm bg-white"
                onMouseDown={startDraw} onMouseMove={draw} onMouseUp={() => setIsDrawing(false)} onMouseLeave={() => setIsDrawing(false)}
                onTouchStart={startDraw} onTouchMove={draw} onTouchEnd={() => setIsDrawing(false)} data-testid="signature-canvas" />
              <button onClick={clear} className="mt-2 text-xs text-slate-500 hover:text-slate-700 flex items-center gap-1" data-testid="clear-signature-btn"><RotateCcw size={12} /> Clear</button>
            </div>
          ) : (
            <input type="text" value={typedSig} onChange={e => setTypedSig(e.target.value)}
              className="w-full px-4 py-4 border border-slate-300 rounded-sm text-2xl focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
              style={{ fontFamily: 'Georgia, serif', fontStyle: 'italic' }} placeholder="Type your signature..." data-testid="typed-signature-input" />
          )}
        </div>
        <div className="flex justify-end gap-3 px-5 py-4 border-t border-slate-200">
          <button onClick={onClose} className="px-4 py-2 border border-slate-300 text-slate-700 rounded-sm text-sm hover:bg-slate-50">Cancel</button>
          <button onClick={accept} className="px-4 py-2 bg-blue-700 text-white rounded-sm text-sm font-medium hover:bg-blue-800" data-testid="accept-signature-btn">Accept &amp; Sign</button>
        </div>
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function SignDocument() {
  const { token } = useParams();

  // Signing data + status
  const [signingData, setSigningData]   = useState(null);
  const [status, setStatus]             = useState(null);

  // Multi-step flow: 'otp_email' → 'otp_verify' → 'consent' → 'signing'
  const [step, setStep]                   = useState('otp_email');
  const [signerEmail, setSignerEmail]     = useState('');
  const [otpCode, setOtpCode]             = useState('');
  const [otpLoading, setOtpLoading]       = useState(false);
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [consentChecked, setConsentChecked] = useState(false);

  // PDF + fields
  const [pages, setPages]           = useState([]);
  const [htmlReady, setHtmlReady]   = useState(false);
  const [fieldValues, setFieldValues] = useState({});
  const [activeModal, setActiveModal] = useState(null);
  const [submitting, setSubmitting]   = useState(false);
  const [pdfError, setPdfError]       = useState(false);
  const canvasRefs    = useRef([]);
  const containerRefs = useRef([]);

  // ── Load signing metadata ────────────────────────────────────────────────
  useEffect(() => {
    fetch(`${BACKEND}/api/sign/${token}`)
      .then(r => r.json())
      .then(data => {
        if (data.status && ['completed', 'voided', 'already_signed'].includes(data.status)) {
          setStatus(data);
        } else if (data.detail) {
          setStatus({ status: 'error', message: data.detail });
        } else {
          setSigningData(data);
          // Pre-fill date fields
          const auto = {};
          (data.fields || []).forEach(f => { if (f.type === 'date') auto[f.id] = new Date().toLocaleDateString('en-US'); });
          setFieldValues(auto);
          // step remains 'otp_email' — PDF loads after consent
        }
      })
      .catch(() => setStatus({ status: 'error', message: 'Failed to load signing page' }));
  }, [token]);

  const isHtmlDoc = signingData?.doc_type === 'html';

  // ── Load PDF (called after consent is accepted) ──────────────────────────
  const loadPdf = async () => {
    try {
      const resp = await fetch(`${BACKEND}/api/sign/${token}/file`);
      if (!resp.ok) throw new Error();
      const buf = await resp.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data: buf }).promise;
      const arr = [];
      for (let i = 1; i <= pdf.numPages; i++) arr.push(await pdf.getPage(i));
      setPages(arr);
    } catch { setPdfError(true); }
  };

  const startSigning = () => {
    setStep('signing');
    if (isHtmlDoc) {
      setHtmlReady(true);
    } else {
      loadPdf();
    }
  };

  // ── Render PDF pages ─────────────────────────────────────────────────────
  useEffect(() => {
    const tasks = [];
    pages.forEach((page, i) => {
      const canvas = canvasRefs.current[i];
      if (!canvas) return;
      const vp = page.getViewport({ scale: 1.4 });
      canvas.width = vp.width; canvas.height = vp.height;
      const task = page.render({ canvasContext: canvas.getContext('2d'), viewport: vp });
      if (task.promise) task.promise.catch(err => { if (err?.name !== 'RenderingCancelledException') console.error(err); });
      tasks.push(task);
    });
    return () => tasks.forEach(t => { try { t.cancel(); } catch (_) {} });
  }, [pages]);

  // ── Auto-scroll to first unsigned field once document loads ──────────────
  useEffect(() => {
    const docReady = isHtmlDoc ? htmlReady : pages.length > 0;
    if (!docReady || !signingData || step !== 'signing') return;
    const first = (signingData.fields || []).find(f => f.required && !fieldValues[f.id]);
    if (first) {
      const timer = setTimeout(() => { const c = containerRefs.current[first.page ?? 0]; if (c) c.scrollIntoView({ behavior: 'smooth', block: 'center' }); }, 1000);
      return () => clearTimeout(timer);
    }
  }, [pages.length, htmlReady, step, isHtmlDoc]);

  // ── OTP handlers ─────────────────────────────────────────────────────────
  const requestOtp = async (e) => {
    e.preventDefault();
    if (!signerEmail.trim()) { toast.error('Please enter your email address'); return; }
    setOtpLoading(true);
    try {
      const resp = await fetch(`${BACKEND}/api/sign/request-otp`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, email: signerEmail }),
      });
      if (!resp.ok) { const e = await resp.json(); throw new Error(e.detail || 'Failed to send OTP'); }
      setStep('otp_verify');
      toast.success(`Verification code sent to ${signerEmail}`);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setOtpLoading(false);
    }
  };

  const verifyOtp = async (e) => {
    e.preventDefault();
    if (!otpCode.trim()) { toast.error('Please enter the verification code'); return; }
    setVerifyLoading(true);
    try {
      const resp = await fetch(`${BACKEND}/api/sign/verify-otp`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, email: signerEmail, otp: otpCode }),
      });
      if (!resp.ok) { const e = await resp.json(); throw new Error(e.detail || 'Invalid code'); }
      setStep('consent');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setVerifyLoading(false);
    }
  };

  // ── Field interaction ─────────────────────────────────────────────────────
  const handleFieldClick = (field) => {
    if (fieldValues[field.id] && field.type !== 'text') return;
    if (field.type === 'signature' || field.type === 'initials') {
      setActiveModal(field);
    } else if (field.type === 'checkbox') {
      setFieldValues(prev => ({ ...prev, [field.id]: prev[field.id] ? '' : 'true' }));
    } else if (field.type === 'text' || field.type === 'name') {
      const val = prompt(`Enter ${field.type}:`);
      if (val !== null) setFieldValues(prev => ({ ...prev, [field.id]: val }));
    }
  };

  const handleSignatureAccept = (fieldId, dataUrl) => { setFieldValues(prev => ({ ...prev, [fieldId]: dataUrl })); setActiveModal(null); };

  const requiredFields      = (signingData?.fields || []).filter(f => f.required);
  const completedRequired   = requiredFields.filter(f => fieldValues[f.id]);
  const allDone             = requiredFields.length === 0 || completedRequired.length === requiredFields.length;

  const scrollToField = useCallback((field, openAfter = true) => {
    const container = containerRefs.current[field.page];
    if (container) container.scrollIntoView({ behavior: 'smooth', block: 'center' });
    if (openAfter) setTimeout(() => handleFieldClick(field), 600);
  }, [fieldValues]);

  const handleSubmit = async () => {
    if (!allDone) { toast.error('Please complete all required fields'); return; }
    setSubmitting(true);
    try {
      const fields = Object.entries(fieldValues).map(([field_id, value]) => ({ field_id, value }));
      const resp = await fetch(`${BACKEND}/api/sign/${token}/submit`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fields, verified_email: signerEmail }),
      });
      if (!resp.ok) throw new Error((await resp.json()).detail || 'Signing failed');
      const result = await resp.json();
      setStatus({
        status: 'just_signed',
        envelope_title: signingData.envelope_title,
        envelope_complete: result.envelope_complete,
        certificate_id: result.certificate_id,
      });
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  // ── Status screens ────────────────────────────────────────────────────────
  if (status) {
    const msgs = {
      completed:    { icon: '✓', color: 'emerald', title: 'Document Completed',   desc: 'This document has been fully signed by all parties.' },
      voided:       { icon: '×', color: 'red',     title: 'Document Voided',      desc: 'This document has been voided and is no longer active.' },
      already_signed: { icon: '✓', color: 'blue',  title: 'Already Signed',       desc: "You've already signed this document." },
      just_signed:  { icon: '✓', color: 'emerald', title: 'Successfully Signed!',
        desc: status?.envelope_complete
          ? 'Document complete! A confirmation email has been sent to all parties.'
          : "Your signature has been recorded. You'll receive a confirmation email when all parties have signed." },
      error:        { icon: '!', color: 'red',     title: 'Error',                desc: status.message || 'Something went wrong.' },
    };
    const m = msgs[status.status] || msgs.error;
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="bg-white border border-slate-200 rounded-sm shadow-sm p-10 max-w-md w-full text-center">
          <div className={`w-16 h-16 rounded-full bg-${m.color}-100 text-${m.color}-600 text-3xl font-bold flex items-center justify-center mx-auto mb-5`}>{m.icon}</div>
          <h1 className="text-xl font-semibold text-slate-900 mb-2" style={{ fontFamily: 'Outfit' }}>{m.title}</h1>
          <p className="text-slate-500 text-sm mb-4">{m.desc}</p>
          {status.envelope_title && <p className="text-xs text-slate-400">Document: {status.envelope_title}</p>}
          {status.status === 'just_signed' && status.certificate_id && (
            <a href={`/verify/${status.certificate_id}`} target="_blank" rel="noreferrer" className="mt-4 inline-block text-blue-700 text-sm hover:underline">View Certificate</a>
          )}
        </div>
      </div>
    );
  }

  // ── Loading ───────────────────────────────────────────────────────────────
  if (!signingData) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-blue-700 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  // ── Shared header for pre-signing steps ──────────────────────────────────
  const PreHeader = () => (
    <div className="bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between">
      <div className="flex items-center gap-2">
        <PenLine size={16} className="text-blue-700" />
        <span className="font-semibold text-slate-900 text-sm" style={{ fontFamily: 'Outfit' }}>SignatureFlow</span>
      </div>
      <p className="text-xs text-slate-500">{signingData.envelope_title}</p>
    </div>
  );

  // ── Step 1: Enter email ───────────────────────────────────────────────────
  if (step === 'otp_email') {
    return (
      <div className="min-h-screen bg-slate-50" data-testid="otp-email-step">
        <PreHeader />
        <div className="flex items-center justify-center p-4 pt-16">
          <div className="bg-white border border-slate-200 rounded-sm shadow-sm p-8 w-full max-w-md">
            <div className="flex justify-center mb-5">
              <div className="w-14 h-14 rounded-full bg-blue-50 flex items-center justify-center">
                <Mail size={26} className="text-blue-700" />
              </div>
            </div>
            <h2 className="text-xl font-semibold text-slate-900 text-center mb-1" style={{ fontFamily: 'Outfit' }}>Verify Your Identity</h2>
            <p className="text-sm text-slate-500 text-center mb-6">Enter your email to receive a one-time verification code before signing.</p>

            <form onSubmit={requestOtp} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Your Email Address</label>
                <input
                  type="email" value={signerEmail}
                  onChange={e => setSignerEmail(e.target.value)}
                  className="w-full px-3 py-2.5 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="you@example.com"
                  autoFocus
                  data-testid="signer-email-input"
                />
              </div>
              <button
                type="submit" disabled={otpLoading}
                className="w-full bg-blue-700 hover:bg-blue-800 disabled:bg-slate-300 disabled:cursor-not-allowed text-white py-2.5 rounded-sm text-sm font-semibold transition-colors"
                data-testid="send-otp-btn"
              >
                {otpLoading ? 'Sending...' : 'Send Verification Code'}
              </button>
            </form>
            <p className="text-xs text-slate-400 text-center mt-4">Your email will be recorded as the verified signer identity.</p>
          </div>
        </div>
      </div>
    );
  }

  // ── Step 2: Enter OTP ─────────────────────────────────────────────────────
  if (step === 'otp_verify') {
    return (
      <div className="min-h-screen bg-slate-50" data-testid="otp-verify-step">
        <PreHeader />
        <div className="flex items-center justify-center p-4 pt-16">
          <div className="bg-white border border-slate-200 rounded-sm shadow-sm p-8 w-full max-w-md">
            <div className="flex justify-center mb-5">
              <div className="w-14 h-14 rounded-full bg-emerald-50 flex items-center justify-center">
                <ShieldCheck size={26} className="text-emerald-600" />
              </div>
            </div>
            <h2 className="text-xl font-semibold text-slate-900 text-center mb-1" style={{ fontFamily: 'Outfit' }}>Enter Verification Code</h2>
            <p className="text-sm text-slate-500 text-center mb-1">A 6-digit code was sent to</p>
            <p className="text-sm font-semibold text-blue-700 text-center mb-6">{signerEmail}</p>

            <form onSubmit={verifyOtp} className="space-y-4">
              <div>
                <input
                  type="text" value={otpCode}
                  onChange={e => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className="w-full px-4 py-3 border border-slate-300 rounded-sm text-2xl text-center font-mono tracking-widest focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="000000"
                  maxLength={6}
                  autoFocus
                  data-testid="otp-code-input"
                />
              </div>
              <button
                type="submit" disabled={verifyLoading || otpCode.length !== 6}
                className="w-full bg-blue-700 hover:bg-blue-800 disabled:bg-slate-300 disabled:cursor-not-allowed text-white py-2.5 rounded-sm text-sm font-semibold transition-colors"
                data-testid="verify-otp-btn"
              >
                {verifyLoading ? 'Verifying...' : 'Verify Code'}
              </button>
            </form>

            <div className="text-center mt-4">
              <button
                onClick={() => { setStep('otp_email'); setOtpCode(''); }}
                className="text-xs text-slate-500 hover:text-blue-700 underline"
                data-testid="resend-otp-btn"
              >
                Resend code or use a different email
              </button>
            </div>
            <p className="text-xs text-slate-400 text-center mt-2">Code expires in 10 minutes.</p>
          </div>
        </div>
      </div>
    );
  }

  // ── Step 3: Legal Consent ─────────────────────────────────────────────────
  if (step === 'consent') {
    return (
      <div className="min-h-screen bg-slate-50" data-testid="consent-step">
        <PreHeader />
        <div className="flex items-center justify-center p-4 pt-16">
          <div className="bg-white border border-slate-200 rounded-sm shadow-sm p-8 w-full max-w-md">
            <div className="flex justify-center mb-5">
              <div className="w-14 h-14 rounded-full bg-blue-50 flex items-center justify-center">
                <FileCheck size={26} className="text-blue-700" />
              </div>
            </div>
            <h2 className="text-xl font-semibold text-slate-900 text-center mb-1" style={{ fontFamily: 'Outfit' }}>Electronic Signature Consent</h2>
            <p className="text-sm text-slate-500 text-center mb-6">Please review and accept before proceeding to sign.</p>

            <div className="bg-slate-50 border border-slate-200 rounded-sm p-4 mb-5 text-sm text-slate-700">
              <p className="font-medium text-slate-800 mb-2">By proceeding, you agree to:</p>
              <ul className="space-y-1.5 text-slate-600 text-sm list-disc list-inside">
                <li>Conduct this transaction electronically</li>
                <li>Use your electronic signature in place of a handwritten signature</li>
                <li>Receive required notices and disclosures electronically</li>
              </ul>
              <p className="mt-3 text-xs text-slate-400">Governed by the ESIGN Act (15 U.S.C. § 7001) and the Uniform Electronic Transactions Act (UETA).</p>
            </div>

            <label className="flex items-start gap-3 cursor-pointer mb-6 p-3 rounded-sm border border-slate-200 hover:border-blue-300 hover:bg-blue-50/30 transition-colors" data-testid="consent-label">
              <input
                type="checkbox" checked={consentChecked}
                onChange={e => setConsentChecked(e.target.checked)}
                className="mt-0.5 w-4 h-4 accent-blue-700"
                data-testid="consent-checkbox"
              />
              <span className="text-sm text-slate-700 leading-relaxed">I agree to conduct business electronically and use electronic signatures as a legally binding equivalent to a handwritten signature.</span>
            </label>

            <button
              onClick={startSigning}
              disabled={!consentChecked}
              className="w-full bg-blue-700 hover:bg-blue-800 disabled:bg-slate-300 disabled:cursor-not-allowed text-white py-2.5 rounded-sm text-sm font-semibold transition-colors"
              data-testid="accept-consent-btn"
            >
              I Agree — Continue to Document
            </button>

            <p className="text-xs text-slate-400 text-center mt-3">
              Signing as: <span className="font-medium text-slate-500">{signerEmail}</span>
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ── Step 4: Signing (existing document view) ──────────────────────────────
  return (
    <div className="min-h-screen bg-slate-100" data-testid="sign-document-page">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between sticky top-0 z-10">
        <div>
          <div className="flex items-center gap-2">
            <PenLine size={16} className="text-blue-700" />
            <span className="font-semibold text-slate-900 text-sm" style={{ fontFamily: 'Outfit' }}>SignatureFlow</span>
          </div>
          <p className="text-xs text-slate-500 mt-0.5">{signingData.envelope_title}</p>
        </div>
        <div className="text-right">
          <p className="text-sm font-medium text-slate-900">{signingData.recipient?.name}</p>
          <p className="text-xs text-slate-400">{signerEmail || signingData.recipient?.email}</p>
        </div>
      </div>

      {/* Message */}
      {signingData.envelope_message && (
        <div className="max-w-3xl mx-auto px-4 pt-4">
          <div className="bg-blue-50 border border-blue-200 rounded-sm px-4 py-3 text-sm text-blue-800">{signingData.envelope_message}</div>
        </div>
      )}

      {/* Document + Fields */}
      <div className="max-w-3xl mx-auto px-4 py-6 flex flex-col items-center gap-6">
        {isHtmlDoc ? (
          <>
            {!htmlReady && (
              <div className="flex flex-col items-center justify-center py-12 gap-3">
                <div className="w-7 h-7 border-2 border-blue-700 border-t-transparent rounded-full animate-spin" />
                <p className="text-sm text-slate-500">Loading document…</p>
              </div>
            )}
            {htmlReady && (
              <div className="relative w-full bg-white shadow-md" ref={el => { containerRefs.current[0] = el; }}>
                <div
                  className="document-preview prose prose-sm max-w-none p-8"
                  dangerouslySetInnerHTML={{ __html: signingData.content_html || '' }}
                />
                {(signingData.fields || []).filter(f => (f.page ?? 0) === 0).map(field => {
                  const style = FIELD_STYLES[field.type] || FIELD_STYLES.signature;
                  const value = fieldValues[field.id];
                  const isSigned = !!value;
                  return (
                    <div
                      key={field.id}
                      onClick={() => !isSigned && handleFieldClick(field)}
                      style={{
                        position: 'absolute', left: `${field.x}%`, top: `${field.y}%`,
                        width: `${field.width}%`, height: `${field.height}%`,
                        transform: 'translate(-50%, -50%)',
                        border: `2px ${isSigned ? 'solid' : 'dashed'} ${style.border}`,
                        background: isSigned ? 'transparent' : style.bg,
                        cursor: isSigned ? 'default' : 'pointer',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        borderRadius: 2,
                        animation: (!isSigned && field.required) ? 'signFieldPulse 2s ease-in-out infinite' : 'none',
                        zIndex: 10,
                      }}
                      data-testid={`signing-field-${field.id}`}
                    >
                      {value ? (
                        value.startsWith('data:image') ? (
                          <img src={value} alt="signature" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                        ) : (
                          <span style={{ fontSize: 12, color: style.border, fontWeight: 600 }}>{value}</span>
                        )
                      ) : (
                        <span style={{ fontSize: 10, color: style.border, fontWeight: 600 }}>
                          {field.type === 'signature' ? 'Sign here' : field.type === 'initials' ? 'Initials' : field.type}
                          {field.required ? ' *' : ''}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </>
        ) : (
          <>
        {(pages.length === 0 && !pdfError) && (
          <div className="flex flex-col items-center justify-center py-12 gap-3">
            <div className="w-7 h-7 border-2 border-blue-700 border-t-transparent rounded-full animate-spin" />
            <p className="text-sm text-slate-500">Loading document…</p>
            {signingData.fields?.length > 0 && (
              <p className="text-xs text-slate-400">
                {signingData.fields.length} field{signingData.fields.length !== 1 ? 's' : ''} to complete
              </p>
            )}
          </div>
        )}
        {(pdfError && pages.length === 0) && (
          <div className="w-full bg-amber-50 border border-amber-200 rounded-sm p-4 text-sm text-amber-700">
            Could not load PDF preview — please complete the fields below and submit.
          </div>
        )}

        {Array.from({ length: Math.max(pages.length, pdfError ? 1 : 0) }).map((_, i) => (
          <div key={i} className="relative w-full bg-white shadow-md" ref={el => containerRefs.current[i] = el}>
            {pages[i] ? (
              <canvas ref={el => canvasRefs.current[i] = el} className="w-full block" />
            ) : (
              <div className="h-48 bg-slate-50 flex items-center justify-center text-slate-400 text-sm">Page {i + 1}</div>
            )}

            <AnnotationLayer
              pageIdx={i}
              annotations={(signingData.annotations || []).filter(a => a.page === i)}
              activeTool={null} activeColor="#000" strokeWidth={2}
              onAdd={() => {}} onRemove={() => {}}
              pdfCanvasRef={{ current: canvasRefs.current[i] }}
              active={false}
            />

            {(signingData.fields || []).filter(f => f.page === i).map(field => {
              const style = FIELD_STYLES[field.type] || FIELD_STYLES.signature;
              const value = fieldValues[field.id];
              const isSigned = !!value;
              return (
                <div
                  key={field.id}
                  onClick={() => !isSigned && handleFieldClick(field)}
                  style={{
                    position: 'absolute', left: `${field.x}%`, top: `${field.y}%`,
                    width: `${field.width}%`, height: `${field.height}%`,
                    transform: 'translate(-50%, -50%)',
                    border: `2px ${isSigned ? 'solid' : 'dashed'} ${style.border}`,
                    background: isSigned ? 'transparent' : style.bg,
                    cursor: isSigned ? 'default' : 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    borderRadius: 2,
                    animation: (!isSigned && field.required) ? 'signFieldPulse 2s ease-in-out infinite' : 'none',
                    zIndex: 10,
                  }}
                  data-testid={`signing-field-${field.id}`}
                >
                  {value ? (
                    value.startsWith('data:image') ? (
                      <img src={value} alt="signature" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                    ) : (
                      <span style={{ fontSize: 12, color: style.border, fontWeight: 600 }}>{value}</span>
                    )
                  ) : (
                    <span style={{ fontSize: 10, color: style.border, fontWeight: 600 }}>
                      {field.type === 'signature' ? 'Sign here' : field.type === 'initials' ? 'Initials' : field.type}
                      {field.required ? ' *' : ''}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        ))}

          </>
        )}

        {/* Required fields panel */}
        {signingData.fields?.length > 0 && (
          <div className="w-full bg-white border border-slate-200 rounded-sm p-5">
            <h3 className="text-sm font-semibold text-slate-900 mb-4">Required Fields</h3>
            <div className="space-y-3">
              {signingData.fields.map(field => {
                const value = fieldValues[field.id];
                return (
                  <div key={field.id} className="flex items-center gap-3">
                    <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${value ? 'bg-emerald-100' : 'bg-slate-100'}`}>
                      {value ? <CheckCircle size={13} className="text-emerald-600" /> : <span className="text-slate-400 text-xs">○</span>}
                    </div>
                    <span className="text-sm text-slate-700 capitalize">{field.type}{field.required ? ' *' : ''}</span>
                    <span className="text-xs text-slate-400 ml-1">{isHtmlDoc ? '' : `Page ${field.page + 1}`}</span>
                    {!value && (
                      <button onClick={() => scrollToField(field)} className="ml-auto text-xs text-blue-700 hover:underline font-medium" data-testid={`complete-field-${field.id}`}>
                        Go to field →
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Floating jump button */}
      {!allDone && (isHtmlDoc ? htmlReady : pages.length > 0) && (() => {
        const next = requiredFields.find(f => !fieldValues[f.id]);
        if (!next) return null;
        return (
          <div style={{ position: 'fixed', bottom: 80, right: 24, zIndex: 50 }}>
            <button
              onClick={() => scrollToField(next)}
              className="flex items-center gap-2 bg-blue-700 hover:bg-blue-800 text-white pl-4 pr-5 py-2.5 rounded-full shadow-lg text-sm font-semibold transition-colors"
              data-testid="jump-to-next-field-btn"
            >
              <ArrowDown size={15} /> {isHtmlDoc ? 'Go to field' : `Sign on page ${next.page + 1}`}
            </button>
          </div>
        );
      })()}

      {/* Bottom action bar */}
      <div className="sticky bottom-0 bg-white border-t border-slate-200 px-4 py-3 flex items-center justify-between shadow-lg" data-testid="sign-action-bar">
        <div className="text-sm text-slate-600">
          <span className="font-semibold text-slate-900">{completedRequired.length}</span>/{requiredFields.length} required fields completed
        </div>
        <button
          onClick={handleSubmit} disabled={!allDone || submitting}
          className="flex items-center gap-2 bg-blue-700 hover:bg-blue-800 disabled:bg-slate-300 disabled:cursor-not-allowed text-white px-6 py-2.5 rounded-sm text-sm font-semibold transition-colors"
          data-testid="finish-signing-btn"
        >
          <CheckCircle size={16} />
          {submitting ? 'Submitting...' : 'Finish Signing'}
        </button>
      </div>

      {/* Signature modal */}
      {activeModal && (
        <SignatureModal field={activeModal} onAccept={handleSignatureAccept} onClose={() => setActiveModal(null)} />
      )}
    </div>
  );
}
